import { Carousel } from '@mantine/carousel'
import { Box, Card, Stack } from '@mantine/core'
import { useTranslations } from 'next-intl'
import Head from 'next/head'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'
import NextSVG from '../components/buttons/next_carousel_button'
import PreviousSVG from '../components/buttons/previous_carousel_button'
import TitleDivider from '../components/divider'
import Layout from '../components/layout'
import TrendingItemsCard from '../components/trending_card'
import styles from '../styles/Home.module.css'
import { searchAPI_v2 } from '../utils/axios'
import prerenderImportantLocales from '../utils/prerender_locales.js'

const genres = [
  'Comedy',
  'Action',
  'Documentary',
  'Anime',
  'Romance',
  'Crime',
  'Drama',
  'Suspense',
  'Horror'
]

const fetchGenreData = async (genre, type, country, platformId, language) => {
  try {
    // Obtener los datos básicos
    const response = await searchAPI_v2.get('/content/hits', {
      params: {
        country,
        type,
        genre,
        platform: platformId,
        language,
        limit: 50,
        append: 'score' // Intentar obtener el score directamente en la primera llamada
      }
    })

    // Verificar si los datos ya incluyen el score
    const hasScores = response.data.data.some((item) => item.score !== undefined)

    if (hasScores) {
      return response.data.data.map((item) => ({
        title: item.translation?.title || item.title,
        poster: item.translation?.poster || item.poster,
        uid: item.uid,
        slug: item.slug,
        content_type: type,
        score: item.score || null
      }))
    }

    // Si no hay scores, obtenerlos con llamadas adicionales
    const basicData = response.data.data.map((item) => ({
      title: item.translation?.title || item.title,
      poster: item.translation?.poster || item.poster,
      uid: item.uid,
      slug: item.slug,
      content_type: type
    }))

    // Obtener los scores para cada elemento
    const detailedData = await Promise.all(
      basicData.map(async (item) => {
        try {
          const metadataResponse = await searchAPI_v2.get('content/metadata/bb', {
            params: {
              id: item.uid,
              type: item.content_type.toLowerCase(),
              append: 'credits,score',
              language
            }
          })
          const score = metadataResponse.data?.score || null
          return {
            ...item,
            score
          }
        } catch (error) {
          return {
            ...item,
            score: null
          }
        }
      })
    )

    return detailedData
  } catch (error) {
    return []
  }
}

const Movies = ({
  moviesByGenre,
  seriesByGenre,
  trendingMovies,
  trendingSeries
}) => {
  const t = useTranslations('Contents')
  const t_index = useTranslations('Index')
  const t_genres = useTranslations('Genres')

  const router = useRouter()
  const { locale } = useRouter()

  const hash = router.asPath.split('#')[1]
  const initialCategory = hash === 'series' ? 'TV Show' : 'Movies'

  const [selectedCategory, setSelectedCategory] = useState(initialCategory)

  const currentDate = new Date()
  const month = currentDate.toLocaleString(locale, { month: 'long' })
  const year = currentDate.getFullYear()

  useEffect(() => {
    if (hash === 'series') {
      setSelectedCategory('TV Show')
    }
  }, [hash])

  const renderContent = () => {
    const initialData = selectedCategory === 'Movies' ? moviesByGenre : seriesByGenre
    return genres.map(
      (genre) =>
        initialData[genre] &&
        initialData[genre].length >= 10 && (
          <Box
            key={genre}
            mt={24}
            sx={(theme) => ({
              width: '80vw',
              margin: 'auto',
              maxWidth: '1366px',
              [theme.fn.smallerThan('md')]: {
                width: '85vw'
              },
              [theme.fn.smallerThan('sm')]: {
                width: '90vw'
              }
            })}
          >
            <TitleDivider
              titleText={`${t('best')} ${t_genres(genre)} ${t(
                'in'
              )} ${month} ${year}`.toUpperCase()}
            />
            <Carousel
              h={222}
              mt={12}
              mb={20}
              slideGap="xs"
              slideSize="10.5%"
              loop={false}
              align="start"
              nextControlIcon={<NextSVG />}
              previousControlIcon={<PreviousSVG />}
              slidesToScroll={1}
              breakpoints={[
                { maxWidth: 'sm', slideSize: '30%', slideGap: 'xs' },
                { maxWidth: 'md', slideSize: '20%', slideGap: 'sm' },
                { maxWidth: 'lg', slideSize: '15%', slideGap: 'md' }
              ]}
              styles={{
                control: {
                  opacity: 1,
                  cursor: 'default',
                  backgroundColor: 'transparent',
                  border: 'transparent',
                  transform: 'none',
                  transition: 'none',

                  '&[data-inactive]': {
                    opacity: 0,
                    cursor: 'default',
                    visibility: 'hidden',
                    pointerEvents: 'none',
                    width: 0,
                    margin: 0
                  },
                  '@media (max-width: 768px)': {
                    display: 'none'
                  }
                }
              }}
            >
              {initialData[genre].map((mov, index) => {
                const poster = mov.translation?.poster || mov.poster
                const title = mov.translation?.title || mov.title
                // Crear una clave única combinando género, tipo de contenido y uid
                const uniqueKey = `${genre}-${selectedCategory.toLowerCase()}-${
                  mov.uid
                }-${index}`
                return (
                  <Carousel.Slide key={uniqueKey}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <Card
                        w={140}
                        h={222}
                        p={0}
                        style={{ width: '140px', height: '222px' }}
                      >
                        <Link
                          href={`/${
                            selectedCategory === 'Movies' ? 'movie' : 'series'
                          }/${mov.slug ? mov.slug : mov.uid}`}
                        >
                          <div
                            style={{
                              width: '100%',
                              height: '100%',
                              position: 'relative'
                            }}
                          >
                            <Image
                              src={`https://images.weserv.nl/?url=${encodeURIComponent(
                                poster
                              )}&w=280&h=444s&fit=cover&output=webp`}
                              fill
                              style={{ objectFit: 'cover' }}
                              alt={title}
                            />
                          </div>
                        </Link>
                      </Card>
                      {mov.score > 0 && (
                        <div
                          style={{
                            backgroundColor: 'rgba(123,57,182,0.2)',
                            color: 'rgba(123,57,182,1)',
                            padding: '2px 6px',
                            fontSize: '10px',
                            fontWeight: '700',
                            letterSpacing: '0.5px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            borderRadius: '4px',
                            marginTop: '8px',
                            alignSelf: 'flex-start'
                          }}
                        >
                          <Image
                            src="/scorestar.png"
                            width={10}
                            height={10}
                            alt="Score star"
                          />
                          {Number(mov.score).toFixed(1)}
                        </div>
                      )}
                    </div>
                  </Carousel.Slide>
                )
              })}
            </Carousel>
          </Box>
        )
    )
  }

  const jsonLdWebPage = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    'name': t_index('pageTitle'),
    'description': t_index('pageDescription'),
    'url': `https://www.streamdiscover.com/${locale}/contents`,
    'inLanguage': locale
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: []
  }

  Object.keys(moviesByGenre || {}).forEach((genre) => {
    moviesByGenre[genre].forEach((item) => {
      jsonLd.itemListElement.push({
        '@type': selectedCategory === 'Movies' ? 'Movie' : 'TVSeries',
        name: item.title,
        image: item.poster,
        genre: genre,
        datePublished: item.year ? item.year.toString() : 'N/A',
        url: `/${selectedCategory === 'Movies' ? 'movie' : 'series'}/${
          item.slug || item.uid
        }`
      })
    })
  })

  return (
    <>
      <Head>
        <title>Contents - Stream Discover</title>
        <meta name="description" content={t_index('pageDescription')} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" href="/favicon.ico" />

        <meta property="og:title" content={t_index('pageTitle')} />
        <meta property="og:description" content={t_index('pageDescription')} />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://www.streamdiscover.com/path/to/preview-image.jpg"
        />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t_index('pageTitle')} />
        <meta name="twitter:description" content={t_index('pageDescription')} />
        <meta
          name="twitter:image"
          content="https://www.streamdiscover.com/path/to/preview-image.jpg"
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdWebPage) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      </Head>
      <main className={styles.main}>
        <Stack
          sx={(theme) => ({
            width: '80vw',
            margin: 'auto',
            maxWidth: '1366px',
            [theme.fn.smallerThan('md')]: {
              width: '85vw'
            },
            [theme.fn.smallerThan('sm')]: {
              width: '90vw'
            }
          })}
        >
          <Box
            sx={(theme) => ({
              marginBottom: '20px',
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              width: '30%',
              marginLeft: 'auto',
              borderRadius: '10px',
              [theme.fn.smallerThan('sm')]: {
                width: '80%',
                justifyContent: 'center',
                margin: 'auto'
              }
            })}
          >
            <button
              className={`${styles.button} ${
                selectedCategory === 'TV Show'
                  ? styles.buttonActive
                  : styles.buttonInactive
              }`}
              onClick={() => setSelectedCategory('TV Show')}
              style={{
                borderRadius: '4px 4px 0px 4px',
                boxShadow: 'inset 0px -3px 0px -1px #7b39b6'
              }}
            >
              {t('tvShowsUppercase')}
            </button>
            <button
              className={`${styles.button} ${
                selectedCategory === 'Movies'
                  ? styles.buttonActive
                  : styles.buttonInactive
              }`}
              onClick={() => setSelectedCategory('Movies')}
              style={{
                borderRadius: '4px 4px 4px 0px',
                boxShadow: 'inset 0px -3px 0px -1px #7b39b6'
              }}
            >
              {t('moviesUppercase')}
            </button>
          </Box>

          <TrendingItemsCard
            contentType={selectedCategory === 'Movies' ? 'movie' : 'series'}
            data={selectedCategory === 'Movies' ? trendingMovies : trendingSeries}
          />
          <Stack mt={20} align="center">
            {renderContent()}
          </Stack>
        </Stack>
      </main>
    </>
  )
}

Movies.getLayout = function getLayout(page) {
  return (
    <Layout withAutocomplete showHero pageName="Contents">
      {page}
    </Layout>
  )
}

export default Movies

export async function getStaticProps({ locale }) {
  prerenderImportantLocales(locale)

  const country = locale.split('-')[1]
  const language = locale.split('-')[0]?.toLowerCase()
  try {
    const moviePromises = genres.map((genre) =>
      fetchGenreData(genre, 'movie', country, null, language)
    )
    const results = await Promise.all(moviePromises)
    const seriesPromises = genres.map((genre) =>
      fetchGenreData(genre, 'series', country, null, language)
    )
    const seriesResults = await Promise.all(seriesPromises)
    const moviesByGenre = genres.reduce((acc, genre, index) => {
      acc[genre] = results[index].filter((item) => item.poster).slice(0, 20)
      return acc
    }, {})
    const seriesByGenre = genres.reduce((acc, genre, index) => {
      acc[genre] = seriesResults[index].filter((item) => item.poster).slice(0, 20)
      return acc
    }, {})

    // Obtener películas trending con score
    const trendingMoviesResponse = await searchAPI_v2.get(
      `/content/hits?country=${country}&type=movie&limit=20&language=${language}&append=score`
    )

    // Verificar si los datos ya incluyen el score
    const hasMovieScores = trendingMoviesResponse.data.data.some(
      (item) => item.score !== undefined
    )

    let trendingMoviesWithScores = []

    if (hasMovieScores) {
      trendingMoviesWithScores = trendingMoviesResponse.data.data.map((item) => ({
        title: item.translation?.title || item.title,
        backdrop: item.translation?.backdrop || item.backdrop,
        uid: item.uid,
        slug: item.slug,
        year: item.year,
        genre: item.genres[0] || '',
        score: item.score || null
      }))
    } else {
      // Obtener scores para películas trending
      trendingMoviesWithScores = await Promise.all(
        trendingMoviesResponse.data.data.map(async (item) => {
          try {
            const metadataResponse = await searchAPI_v2.get('content/metadata/bb', {
              params: {
                id: item.uid,
                type: 'movie',
                append: 'credits,score',
                language
              }
            })
            const score = metadataResponse.data?.score || null
            return {
              title: item.translation?.title || item.title,
              backdrop: item.translation?.backdrop || item.backdrop,
              uid: item.uid,
              slug: item.slug,
              year: item.year,
              genre: item.genres[0] || '',
              score
            }
          } catch (error) {
            return {
              title: item.translation?.title || item.title,
              backdrop: item.translation?.backdrop || item.backdrop,
              uid: item.uid,
              slug: item.slug,
              year: item.year,
              genre: item.genres[0] || '',
              score: null
            }
          }
        })
      )
    }

    // Obtener series trending con score
    const trendingSeriesResponse = await searchAPI_v2.get(
      `/content/hits?country=${country}&type=series&limit=20&language=${language}&append=score`
    )

    // Verificar si los datos ya incluyen el score
    const hasSeriesScores = trendingSeriesResponse.data.data.some(
      (item) => item.score !== undefined
    )

    let trendingSeriesWithScores = []

    if (hasSeriesScores) {
      trendingSeriesWithScores = trendingSeriesResponse.data.data.map((item) => ({
        title: item.translation?.title || item.title,
        poster: item.translation?.poster || item.poster,
        uid: item.uid,
        slug: item.slug,
        year: item.year,
        genre: item.genres[0] || '',
        score: item.score || null
      }))
    } else {
      // Obtener scores para series trending
      trendingSeriesWithScores = await Promise.all(
        trendingSeriesResponse.data.data.map(async (item) => {
          try {
            const metadataResponse = await searchAPI_v2.get('content/metadata/bb', {
              params: {
                id: item.uid,
                type: 'series',
                append: 'credits,score',
                language
              }
            })
            const score = metadataResponse.data?.score || null
            return {
              title: item.translation?.title || item.title,
              poster: item.translation?.poster || item.poster,
              uid: item.uid,
              slug: item.slug,
              year: item.year,
              genre: item.genres[0] || '',
              score
            }
          } catch (error) {
            return {
              title: item.translation?.title || item.title,
              poster: item.translation?.poster || item.poster,
              uid: item.uid,
              slug: item.slug,
              year: item.year,
              genre: item.genres[0] || '',
              score: null
            }
          }
        })
      )
    }

    const translations = (
      await import(`../locales/${locale.split('-')[0]}/common.json`)
    ).default

    const messages = {
      Contents: translations.Contents,
      Genres: translations.Genres,
      Index: translations.Index,
      Navbar: translations.Navbar,
      Footer: translations.Footer
    }

    return {
      props: {
        moviesByGenre,
        seriesByGenre,
        trendingMovies: trendingMoviesWithScores,
        trendingSeries: trendingSeriesWithScores,
        messages
      },
      revalidate: 60 * 60 * 24
    }
  } catch (error) {
    return {
      props: {
        initialData: null,
        messages: (await import(`../locales/${locale.split('-')[0]}/common.json`))
          .default
      },
      revalidate: 60 * 60 * 24
    }
  }
}
